import React, { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  ActionList,
  ActionListItem,
  Amount,
  Badge,
  Box,
  Button,
  CheckCircle2Icon,
  ChevronDownIcon,
  Link,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  Heading,
  ProgressBar,
  RefreshIcon,
  Skeleton,
  SubscriptionsIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
} from '@razorpay/blade/components';
import { useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { EmptyView } from 'components/SalaryComponents/components/EmptyView';
import { DetailsSkeleton } from 'components/ui/Skeletons/DetailsSkeleton';
import {
  AdminLoanDrawerProps,
  LoanDetail,
  RecoveryScheduleEntry,
  RecoveryScheduleTableNode,
} from './types';
import {
  ADMIN_ACTIVE_LOAN_KEY,
  EMI_STATUS_COLOR,
  EMI_STATUS_LABEL,
  INTEREST_TYPE_LABEL,
  RECOVERY_PREVIEW_KEY,
} from './constants';
import {
  LOAN_STATUS,
  LOAN_STATUS_COLOR,
  LOAN_STATUS_LABEL,
} from 'components/LoansAndAdvances/constants';
import { formatMonthYearShort, normalizeLoanDetail } from './utils';
import { DetailRow } from './DetailRow';
import { SkipEmiModal } from './SkipEmiModal';
import { ModifyDurationModal } from './ModifyDurationModal';
import { RecordRecoveryModal } from './RecordRecoveryModal';

export const AdminLoanDrawer = ({
  isOpen,
  onDismiss,
  loanId,
  showActions = true,
}: AdminLoanDrawerProps) => {
  const queryClient = useQueryClient();
  const [isSkipEmiOpen, setIsSkipEmiOpen] = useState(false);
  const [isModifyDurationOpen, setIsModifyDurationOpen] = useState(false);
  const [isRecoveryOpen, setIsRecoveryOpen] = useState(false);

  const activeDrawerQueryKey = [ADMIN_ACTIVE_LOAN_KEY, loanId] as const;
  const recoveryPreviewQueryKey = [RECOVERY_PREVIEW_KEY, loanId] as const;

  const {
    data: loanData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [ADMIN_ACTIVE_LOAN_KEY, loanId],
    queryFn: () => api.loans.getLoanDetail(loanId!),
    enabled: !!loanId && isOpen,
  });
  const loan = normalizeLoanDetail<LoanDetail>(loanData);

  const { data: recoveryData } = useQuery({
    queryKey: [RECOVERY_PREVIEW_KEY, loanId],
    queryFn: () => api.loans.recoveryPreview(loanId!),
    enabled: !!loanId && isOpen && !!loan,
  });
  const schedule: RecoveryScheduleEntry[] = recoveryData?.data ?? [];

  const repaidPercent = loan?.total_amount
    ? Math.round(((loan.amount_paid ?? 0) / loan.total_amount) * 100)
    : 0;

  const handleModalSuccess = () => {
    onDismiss();
    queryClient.invalidateQueries(['LOANS']);
  };

  const scheduleNodes: RecoveryScheduleTableNode[] = schedule.map((entry, idx) => ({
    ...entry,
    id: entry.id ?? idx,
  }));

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss}>
        {!isLoading ? (
          <DrawerHeader
            title={
              loan
                ? LOAN_STATUS_LABEL[loan.status] ?? loan.name ?? ''
                : isError
                ? 'Loan Details'
                : ''
            }
            color={loan ? LOAN_STATUS_COLOR[loan.status] ?? 'neutral' : 'neutral'}>
            <>
              {isLoading && (
                <Box
                  paddingX="spacing.4"
                  paddingY="spacing.5"
                  display="flex"
                  flexDirection="column"
                  gap="spacing.4">
                  <Skeleton height="spacing.7" width="50%" />
                  <Skeleton height="spacing.7" />
                </Box>
              )}
              {loan && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap="spacing.6"
                  paddingTop="spacing.6">
                  {loan.status === LOAN_STATUS.CLOSED ? (
                    <CheckCircle2Icon color="feedback.icon.positive.intense" size="2xlarge" />
                  ) : (
                    <SubscriptionsIcon color="feedback.icon.information.intense" size="2xlarge" />
                  )}
                  <Amount value={loan.principal} size="2xlarge" type="heading" weight="semibold" />
                  <Badge color={LOAN_STATUS_COLOR[loan.status] ?? 'neutral'} size="large">
                    {LOAN_STATUS_LABEL[loan.status] ?? 'Active'}
                  </Badge>

                  {loan.status === LOAN_STATUS.CLOSED ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="spacing.3"
                      width="100%"
                      alignItems="center">
                      <Heading size="small" weight="regular" color="surface.text.gray.subtle">
                        Loan has been closed and cannot be deleted
                      </Heading>
                      <Text size="small" color="surface.text.gray.muted" textAlign="center">
                        {`#${loan.id}`}
                        {loan.updated_at
                          ? ` • Closed on ${new Date(loan.updated_at).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}`
                          : ''}
                      </Text>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap="spacing.4" width="100%">
                      <Box display="flex" justifyContent="space-between">
                        <Box
                          paddingLeft="spacing.3"
                          borderLeftWidth="thick"
                          borderLeftColor="surface.border.gray.muted">
                          <Text size="medium" weight="medium" color="surface.text.gray.muted">
                            Repaid Amount
                          </Text>
                          <Amount value={loan.amount_paid ?? 0} size="medium" />
                        </Box>
                        <Box
                          textAlign="right"
                          paddingLeft="spacing.3"
                          borderLeftWidth="thick"
                          borderLeftColor="surface.border.gray.muted">
                          <Text size="medium" weight="medium" color="surface.text.gray.muted">
                            Outstanding Amount
                          </Text>
                          <Amount value={loan.remaining_amount_to_be_paid ?? 0} size="medium" />
                        </Box>
                      </Box>
                      <ProgressBar accessibilityLabel="Repayment progress" value={repaidPercent} />
                    </Box>
                  )}

                  {showActions && (
                    <Box display="flex" gap="spacing.3" width="100%">
                      <Box flex="1" display="flex" borderRadius="medium" overflow="hidden">
                        <Box flex="1">
                          <Button isFullWidth variant="tertiary" onClick={()=> setIsSkipEmiOpen(true)}>
                            Skip EMI
                          </Button>
                        </Box>
                        <Box borderLeftWidth="thinner">
                          <Dropdown>
                            <DropdownButton variant="tertiary" icon={ChevronDownIcon} />
                            <DropdownOverlay defaultPlacement="bottom-end">
                              <ActionList>
                                {[
                                  <ActionListItem
                                    key="modify"
                                    title="Modify Duration"
                                    value="modify"
                                    onClick={()=> setIsModifyDurationOpen(true)}
                                  />,
                                ]}
                              </ActionList>
                            </DropdownOverlay>
                          </Dropdown>
                        </Box>
                      </Box>
                      <Box flex="1">
                        <Button isFullWidth onClick={()=> setIsRecoveryOpen(true)}>
                          Record Recovery
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          </DrawerHeader>
        ) : null}

        <DrawerBody>
          {isLoading && (
            <Box paddingX="spacing.4" display="flex" flexDirection="column" gap="spacing.6">
              <DetailsSkeleton rowCount={6} />
            </Box>
          )}
          {isError && (
            <EmptyView
              isError
              description={
                <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
                  Could not load loan details. Please try again.
                </Text>
              }>
              <Box display="flex" justifyContent="center">
                <Button icon={RefreshIcon} size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              </Box>
            </EmptyView>
          )}

          {loan && (
            <Box display="flex" flexDirection="column" gap="spacing.6">
              <Box backgroundColor="surface.background.gray.moderate">
                <Accordion defaultExpandedIndex={0} size="medium">
                  <AccordionItem>
                    <AccordionItemHeader title="Details" />
                    <AccordionItemBody>
                      <Box display="flex" flexDirection="column" gap="spacing.5">
                        {loan.status === LOAN_STATUS.CLOSED ? (
                          <>
                            <DetailRow
                              label="Loan amount"
                              value={<Amount value={loan.principal} size="small" />}
                            />
                            <DetailRow
                              label="Total (P + I)"
                              value={<Amount value={loan.total_amount ?? 0} size="small" />}
                            />
                            <DetailRow
                              label="Interest type"
                              value={
                                loan.interest_type != null
                                  ? INTEREST_TYPE_LABEL[loan.interest_type] ?? '—'
                                  : '—'
                              }
                            />
                            <DetailRow
                              label="Rate of Interest"
                              value={`${loan.interest_rate ?? 0}%`}
                            />
                            <DetailRow label="Perquisite" value={`${loan.perk_rate ?? 0}%`} />
                            {loan.recovery_start_from && (
                              <DetailRow
                                label="EMI start month"
                                value={new Date(loan.recovery_start_from).toLocaleDateString(
                                  'en-IN',
                                  { month: 'long', year: 'numeric' },
                                )}
                              />
                            )}
                            <DetailRow
                              label="Monthly EMI"
                              value={<Amount value={loan.monthly_total_emi ?? 0} size="small" />}
                            />
                          </>
                        ) : (
                          <>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Text size="small" weight="medium" color="surface.text.gray.muted">
                                Employee Name
                              </Text>
                              <Link size="small" href={`/employeeDetail?userId=${loan.people_id}`}>
                                {loan.name ?? '—'}
                              </Link>
                            </Box>
                            <DetailRow label="ID" value={`#${loan.id}`} />
                            <DetailRow label="Reason" value={loan.loan_category_name ?? '—'} />
                            <DetailRow
                              label="Amount"
                              value={<Amount value={loan.principal} size="small" />}
                            />
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start">
                              <Box>
                                <Text size="small" weight="medium" color="surface.text.gray.muted">
                                  Total Repayable
                                </Text>
                                <Text size="xsmall" color="surface.text.gray.muted">
                                  (Principal + Interest)
                                </Text>
                              </Box>
                              <Amount value={loan.total_amount ?? 0} size="small" />
                            </Box>
                            <DetailRow
                              label="Interest Type"
                              value={
                                loan.interest_type != null
                                  ? INTEREST_TYPE_LABEL[loan.interest_type] ?? '—'
                                  : '—'
                              }
                            />
                            {(loan.interest_rate ?? 0) > 0 && (
                              <DetailRow
                                label="Rate of Interest (p.a.)"
                                value={`${loan.interest_rate}%`}
                              />
                            )}
                            {(loan.perk_rate ?? 0) > 0 && (
                              <DetailRow label="Perquisite Rate" value={`${loan.perk_rate}%`} />
                            )}
                          </>
                        )}
                      </Box>
                    </AccordionItemBody>
                  </AccordionItem>
                </Accordion>
              </Box>

              {schedule.length > 0 && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader title="Repayment Schedule" />
                      <AccordionItemBody>
                        <Table<RecoveryScheduleTableNode>
                          data={{ nodes: scheduleNodes }}
                          gridTemplateColumns="1fr 1fr 1fr">
                          {(tableData) => (
                            <>
                              <TableHeader>
                                <TableHeaderRow>
                                  <TableHeaderCell>Month</TableHeaderCell>
                                  <TableHeaderCell textAlign="right">EMI</TableHeaderCell>
                                  <TableHeaderCell>Status</TableHeaderCell>
                                </TableHeaderRow>
                              </TableHeader>
                              <TableBody>
                                {tableData.map((entry) => (
                                  <TableRow key={entry.id} item={entry}>
                                    <TableCell>
                                      <Text size="small" color="surface.text.gray.subtle">
                                        {formatMonthYearShort(entry.payroll_month ?? entry.month)}
                                      </Text>
                                    </TableCell>
                                    <TableCell textAlign="right">
                                      <Amount
                                        value={entry.emi_amount ?? entry.total_emi ?? 0}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        color={EMI_STATUS_COLOR[entry.status] ?? 'neutral'}
                                        size="small">
                                        {EMI_STATUS_LABEL[entry.status] ?? 'Pending'}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </>
                          )}
                        </Table>
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}
            </Box>
          )}
        </DrawerBody>
      </Drawer>

      <SkipEmiModal
        isOpen={isSkipEmiOpen}
        onDismiss={() => setIsSkipEmiOpen(false)}
        loanId={loanId}
        loan={loan}
        onSuccess={handleModalSuccess}
        activeDrawerQueryKey={activeDrawerQueryKey}
        recoveryPreviewQueryKey={recoveryPreviewQueryKey}
      />

      <ModifyDurationModal
        isOpen={isModifyDurationOpen}
        onDismiss={() => setIsModifyDurationOpen(false)}
        loanId={loanId}
        onSuccess={handleModalSuccess}
        activeDrawerQueryKey={activeDrawerQueryKey}
        recoveryPreviewQueryKey={recoveryPreviewQueryKey}
      />

      <RecordRecoveryModal
        isOpen={isRecoveryOpen}
        onDismiss={() => setIsRecoveryOpen(false)}
        loanId={loanId}
        loan={loan}
        onSuccess={handleModalSuccess}
        activeDrawerQueryKey={activeDrawerQueryKey}
      />
    </>
  );
};
